import { useEffect, useState } from "react";
import { CrudPanel } from "../components/CrudPanel";
import { staffService } from "../services/staffService";
import { propertyService } from "../services/propertyService";
import { Users } from "lucide-react";
import { mobileError, sanitizeMobile, MOBILE_LENGTH } from "../utils/validation";

const emptyStaff = {
  firstName: "",
  lastName: "",
  mobile: "",
  email: "",
  govtId: "",
  roleName: "",
  username: "",
  password: "",
  tenantId: null,
  propertyUuid: "",
};

const defaultRoles = ["Super Admin", "Admin", "Tenant", "Agent", "Staff"];

export function Staff({ notify, setHeaderAction, tenantId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyStaff);
  const [showForm, setShowForm] = useState(false);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const formFields = ["firstName", "lastName", "mobile", "email", "govtId", "roleName", "username", "password", "propertyUuid", "isDeleted"];
  const isTenant = tenantId && tenantId !== "null";
  const allowedRoles = isTenant ? defaultRoles.filter(r => r !== "Super Admin" && r !== "Agent") : defaultRoles;

  useEffect(() => {
    propertyService.listForTenant(tenantId)
      .then((list) => setPropertyOptions(list.map((p) => ({ value: p.uuid, label: p.name }))))
      .catch(() => setPropertyOptions([]));
  }, [tenantId]);

  useEffect(() => {
    if (setHeaderAction) {
      setHeaderAction(
        <button
          className={showForm ? "secondary" : "primary"}
          onClick={() => setShowForm(!showForm)}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {showForm ? "Hide" : "Add"} Staff
        </button>
      );
    }
    return () => { if (setHeaderAction) setHeaderAction(null); };
  }, [showForm, setHeaderAction]);

  const load = async () => {
    setLoading(true);
    try {
      const result = await staffService.list(tenantId);
      setRows(result);
    } catch (err) {
      notify(err?.message || "Failed to load staff list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId]);

  async function save(event) {
    event.preventDefault();
    if (saving) return;
    const mobileErr = mobileError(form.mobile);
    if (mobileErr) {
      setErrors({ mobile: mobileErr });
      notify(mobileErr);
      return;
    }
    setSaving(true);
    try {
      await staffService.save({ ...form, tenantId, propertyId: form.propertyUuid || null });
      setForm(emptyStaff);
      setShowForm(false);
      setErrors({});
      notify("Staff member saved.");
      load();
    } catch (err) {
      console.error("Staff save error:", err);
      notify(err?.message || "Failed to save staff member.");
    } finally {
      setSaving(false);
    }
  }

  function handleFieldChange(key, value) {
    if (errors[key]) {
      setErrors((current) => ({ ...current, [key]: undefined }));
    }
    if (key === "mobile" && value) {
      setForm((prev) => ({ ...prev, username: value }));
    }
  }

  async function remove(uuid) {
    if (!window.confirm("Delete this staff member?")) return;
    try {
      await staffService.remove(uuid);
      notify("Staff member deleted.");
      load();
    } catch (err) {
      console.error("Staff delete error:", err);
      notify(err?.message || "Failed to delete staff member.");
    }
  }

  async function restore(uuid) {
    try {
      await staffService.restore(uuid);
      notify("Staff member restored.");
      load();
    } catch (err) {
      console.error("Staff restore error:", err);
      notify(err?.message || "Failed to restore staff member.");
    }
  }

  return (
    <CrudPanel
      title="Staff"
      sectionLabel={<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Users size={18} />Staff Management</span>}
      header="Staff Members"
      rows={rows}
      loading={loading}
      saving={saving}
      form={form}
      setForm={setForm}
      emptyForm={emptyStaff}
      save={save}
      remove={remove}
      fields={formFields}
      firstRowFields={["firstName", "lastName", "mobile"]}
      hideTableFields={["password", "tenantId", "propertyId", "propertyUuid"]}
      hideFormFields={["isDeleted"]}
      passwordFields={["password"]}
      requiredFields={["firstName", "mobile", "govtId", "roleName", "username", "propertyUuid", ...(form.uuid ? [] : ["password"])]}
      selectFields={{ roleName: allowedRoles, propertyUuid: propertyOptions }}
      fieldLabels={{ propertyUuid: "Property", tenantId: "Tenant" }}
      columnLabels={{ isDeleted: "Deleted" }}
      telFields={["mobile"]}
      sanitizeFields={{ mobile: sanitizeMobile }}
      onFieldChange={handleFieldChange}
      onRestore={restore}
      collapsible={true}
      showForm={showForm}
      setShowForm={setShowForm}
      buttonLabel="Staff"
      colorFields={{ roleName: "rgb(29 78 216 / var(--tw-text-opacity, 1))" }}
      bgColorFields={{ roleName: "rgb(219 234 254 / var(--tw-bg-opacity, 1))" }}
    />
  );
}